import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { useControllableState } from '../../hooks/useControllableState.js';
import { useId } from '../../hooks/useId.js';

export type NumberInputSize = 'sm' | 'md' | 'lg';

export interface NumberInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'defaultValue' | 'onChange' | 'size' | 'type' | 'min' | 'max' | 'step' | 'prefix'
> {
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Round displayed + emitted values to this precision. Default: inferred
   *  from `step` (0.1 → 1 decimal, 0.01 → 2, etc.). */
  precision?: number;
  /** Suffix text shown inside the field (e.g. `"%"`, `"px"`). Non-interactive. */
  suffix?: ReactNode;
  prefix?: ReactNode;
  size?: NumberInputSize;
  /** When true, holding the step button accelerates after 400 ms. Default true. */
  hold?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  ref?: Ref<HTMLInputElement>;
  className?: string;
}

function inferPrecision(step: number): number {
  const str = String(step);
  const dot = str.indexOf('.');
  return dot === -1 ? 0 : str.length - dot - 1;
}

function roundTo(n: number, precision: number): number {
  const f = 10 ** precision;
  return Math.round(n * f) / f;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/** Numeric input with a step rail (▲ / ▼), keyboard arrow increments,
 *  min/max clamping, and optional hold-to-accelerate on the step buttons.
 *
 *  Why not `<input type="number">`: the native spinner is unstyled, the
 *  clamping behaviour differs across browsers, and floating-point
 *  arithmetic (`0.1 + 0.2`) produces display noise without a precision
 *  hint. This component normalises all of those while still using a
 *  `type="text"` input with `inputmode="decimal"` for mobile keyboards.
 */
export function NumberInput(props: NumberInputProps): ReactNode {
  const {
    value,
    defaultValue,
    onChange,
    min = -Infinity,
    max = Infinity,
    step = 1,
    precision,
    suffix,
    prefix,
    size = 'md',
    hold = true,
    invalid,
    disabled,
    readOnly,
    ref,
    className,
    id,
    onBlur,
    ...rest
  } = props;

  const resolvedPrecision = precision ?? inferPrecision(step);
  const [current, setCurrent] = useControllableState<number>({
    defaultValue: defaultValue ?? 0,
    ...(value !== undefined ? { value } : {}),
    ...(onChange !== undefined ? { onChange } : {}),
  });

  /* Edit-draft string — lets users type intermediate states like "-"
   * or "3." without the controlled value snapping them back. Committed
   * on blur or when the parsed value becomes valid. */
  const [draft, setDraft] = useState<string | null>(null);
  const displayed = draft ?? String(roundTo(current, resolvedPrecision));

  const inputId = useId(id ?? 'num');

  /* Step / precision normalisation. An absolute zero step would make
   * the step buttons a no-op and — paired with the hold-to-accelerate
   * setInterval — would fire 16× / sec forever without visible effect.
   * Sub-perceptible negative steps flip direction; we coerce to abs. */
  const safeStep = step === 0 ? 1 : Math.abs(step);
  /* Validated bounds — reject min > max and fall back to open range to
   * avoid a component that's locked into its own `max` forever. */
  const safeMin = min <= max ? min : -Infinity;
  const safeMax = min <= max ? max : Infinity;

  const commit = useCallback(
    (raw: string) => {
      const parsed = parseFloat(raw);
      if (Number.isNaN(parsed)) {
        setDraft(null);
        return;
      }
      setCurrent(clamp(roundTo(parsed, resolvedPrecision), safeMin, safeMax));
      setDraft(null);
    },
    [setCurrent, resolvedPrecision, safeMin, safeMax],
  );

  const nudge = useCallback(
    (dir: 1 | -1) => {
      setCurrent(clamp(roundTo(current + dir * safeStep, resolvedPrecision), safeMin, safeMax));
    },
    [current, safeStep, resolvedPrecision, safeMin, safeMax, setCurrent],
  );

  const holdTimer = useRef<number | null>(null);
  const holdInterval = useRef<number | null>(null);
  const startHold = useCallback(
    (dir: 1 | -1) => {
      if (!hold) return;
      holdTimer.current = window.setTimeout(() => {
        holdInterval.current = window.setInterval(() => {
          nudge(dir);
        }, 60);
      }, 400);
    },
    [hold, nudge],
  );
  const stopHold = useCallback(() => {
    if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
    if (holdInterval.current !== null) window.clearInterval(holdInterval.current);
    holdTimer.current = null;
    holdInterval.current = null;
  }, []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (readOnly === true || disabled === true) return;
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        nudge(1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        nudge(-1);
      } else if (e.key === 'Home' && safeMin !== -Infinity) {
        e.preventDefault();
        setCurrent(safeMin);
      } else if (e.key === 'End' && safeMax !== Infinity) {
        e.preventDefault();
        setCurrent(safeMax);
      } else if (e.key === 'Enter') {
        /* Prevent form submit when wrapped in a <form>; committing the
         * value is the user's intent here, not a submit. */
        e.preventDefault();
        commit((e.currentTarget as HTMLInputElement).value);
      }
    },
    [readOnly, disabled, nudge, safeMin, safeMax, setCurrent, commit],
  );

  const onInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
  }, []);

  const onInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      commit(e.target.value);
      onBlur?.(e);
    },
    [commit, onBlur],
  );

  const atMax = current >= safeMax;
  const atMin = current <= safeMin;

  const classes = [
    'vq-num',
    `vq-num--${size}`,
    invalid === true && 'vq-num--invalid',
    disabled === true && 'vq-num--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      data-component="NumberInput"
      data-state={disabled === true ? 'disabled' : invalid === true ? 'invalid' : 'idle'}
    >
      {prefix !== undefined && <span className="vq-num__prefix">{prefix}</span>}
      <input
        {...rest}
        ref={ref}
        id={inputId}
        type="text"
        inputMode="decimal"
        className="vq-num__input"
        value={displayed}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        onBlur={onInputBlur}
        aria-invalid={invalid}
        disabled={disabled}
        readOnly={readOnly}
        role="spinbutton"
        aria-valuenow={Number.isFinite(current) ? current : undefined}
        aria-valuemin={Number.isFinite(safeMin) ? safeMin : undefined}
        aria-valuemax={Number.isFinite(safeMax) ? safeMax : undefined}
      />
      {suffix !== undefined && <span className="vq-num__suffix">{suffix}</span>}
      <div className="vq-num__steps">
        <button
          type="button"
          className="vq-num__step"
          tabIndex={-1}
          disabled={disabled === true || readOnly === true || atMax}
          aria-label="Increment"
          onClick={() => {
            nudge(1);
          }}
          onPointerDown={() => {
            startHold(1);
          }}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
        >
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
            <polyline
              points="1 5 5 1 9 5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          className="vq-num__step"
          tabIndex={-1}
          disabled={disabled === true || readOnly === true || atMin}
          aria-label="Decrement"
          onClick={() => {
            nudge(-1);
          }}
          onPointerDown={() => {
            startHold(-1);
          }}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
        >
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
            <polyline
              points="1 1 5 5 9 1"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
